import React from 'react';
import Icon from '../../../components/AppIcon';

const TransactionDetails = ({ 
  selectedNode, 
  onClose,
  className = '' 
}) => {
  if (!selectedNode) {
    return (
      <div className={`bg-card border border-border rounded-lg p-6 ${className}`}>
        <div className="text-center text-muted-foreground">
          <Icon name="MousePointer" size={48} className="mx-auto mb-4 opacity-50" />
          <h3 className="text-lg font-medium mb-2">Select a Transaction</h3>
          <p className="text-sm">
            Click on any node in the provenance graph to view detailed transaction information.
          </p>
        </div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified': return 'text-success';
      case 'completed': return 'text-success';
      case 'active': return 'text-warning';
      case 'retired': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified': return 'CheckCircle';
      case 'completed': return 'CheckCircle';
      case 'active': return 'Clock';
      case 'retired': return 'Archive';
      default: return 'Circle';
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp)?.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };

  const renderNodeSpecificDetails = () => {
    switch (selectedNode?.type) {
      case 'issuance':
        return (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Registry</label>
              <p className="text-sm text-foreground">{selectedNode?.registry}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Serial Number</label>
              <p className="text-sm font-mono text-foreground">{selectedNode?.serialNumber}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Amount Issued</label>
              <p className="text-sm text-foreground">{selectedNode?.amount?.toLocaleString()} tCO2e</p>
            </div>
          </div>
        );

      case 'transfer':
        return (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">From</label>
              <p className="text-sm text-foreground">{selectedNode?.from}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">To</label>
              <p className="text-sm text-foreground">{selectedNode?.to}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Amount Transferred</label>
              <p className="text-sm text-foreground">{selectedNode?.amount?.toLocaleString()} tCO2e</p>
            </div>
          </div>
        );

      case 'tokenization':
        return (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Blockchain Platform</label>
              <p className="text-sm text-foreground">{selectedNode?.platform}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Contract Address</label>
              <p className="text-sm font-mono text-foreground break-all">{selectedNode?.contractAddress}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Tokens Minted</label>
              <p className="text-sm text-foreground">{selectedNode?.amount?.toLocaleString()} tokens</p>
            </div>
            <div className="bg-warning/10 border border-warning/20 rounded-lg p-3">
              <div className="flex items-start space-x-2">
                <Icon name="AlertTriangle" size={16} className="text-warning mt-0.5" />
                <div>
                  <p className="text-xs font-medium text-warning">Tokenization Alert</p>
                  <p className="text-xs text-warning/80">
                    Credits are now represented as blockchain tokens. Ensure proper custody and avoid re-wrapping.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'split':
        return (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Original Amount</label>
              <p className="text-sm text-foreground">{selectedNode?.originalAmount?.toLocaleString()} tCO2e</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Split Into</label>
              <div className="space-y-1">
                {selectedNode?.splitAmounts?.map((amount, index) => (
                  <p key={index} className="text-sm text-foreground">
                    Batch {index + 1}: {amount?.toLocaleString()} tCO2e
                  </p>
                ))}
              </div>
            </div>
          </div>
        );

      case 'retirement':
        return (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Retiring Company</label>
              <p className="text-sm text-foreground">{selectedNode?.company}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Claim Type</label>
              <p className="text-sm text-foreground">{selectedNode?.claimType}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Amount Retired</label>
              <p className="text-sm text-foreground">{selectedNode?.amount?.toLocaleString()} tCO2e</p>
            </div>
            <div className="bg-error/10 border border-error/20 rounded-lg p-3">
              <div className="flex items-start space-x-2">
                <Icon name="Archive" size={16} className="text-error mt-0.5" />
                <div>
                  <p className="text-xs font-medium text-error">Permanent Retirement</p>
                  <p className="text-xs text-error/80">
                    These credits have been permanently retired and cannot be transferred or traded.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={`bg-card border border-border rounded-lg ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-3">
          <div 
            className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
            style={{ backgroundColor: selectedNode?.type === 'issuance' ? '#52B788' :
                                     selectedNode?.type === 'transfer' ? '#2D6A4F' :
                                     selectedNode?.type === 'tokenization' ? '#F77F00' :
                                     selectedNode?.type === 'split' ? '#1B4332' : '#D62828' }}
          >
            {selectedNode?.type === 'issuance' ? 'I' :
             selectedNode?.type === 'transfer' ? 'T' :
             selectedNode?.type === 'tokenization' ? 'B' :
             selectedNode?.type === 'split' ? 'S' : 'R'}
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">{selectedNode?.label}</h3>
            <div className="flex items-center space-x-2">
              <Icon 
                name={getStatusIcon(selectedNode?.status)} 
                size={14} 
                className={getStatusColor(selectedNode?.status)} 
              />
              <span className={`text-xs font-medium capitalize ${getStatusColor(selectedNode?.status)}`}>
                {selectedNode?.status}
              </span>
            </div>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-muted rounded-lg transition-smooth"
        >
          <Icon name="X" size={20} />
        </button>
      </div>
      {/* Content */}
      <div className="p-4 space-y-6">
        {/* Basic Information */}
        <div>
          <h4 className="text-sm font-semibold text-foreground mb-3">Transaction Details</h4>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Transaction ID</label>
              <p className="text-sm font-mono text-foreground">{selectedNode?.id}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Timestamp</label>
              <p className="text-sm text-foreground">{formatTimestamp(selectedNode?.timestamp)}</p>
            </div>
          </div>
        </div>

        {/* Node-specific Details */}
        <div>
          <h4 className="text-sm font-semibold text-foreground mb-3">Specific Information</h4>
          {renderNodeSpecificDetails()}
        </div>

        {/* Verification Status */}
        <div>
          <h4 className="text-sm font-semibold text-foreground mb-3">Verification</h4>
          <div className="bg-success/10 border border-success/20 rounded-lg p-3">
            <div className="flex items-start space-x-2">
              <Icon name="Shield" size={16} className="text-success mt-0.5" />
              <div>
                <p className="text-xs font-medium text-success">Verified Transaction</p>
                <p className="text-xs text-success/80">
                  This transaction has been cryptographically verified and recorded immutably.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex space-x-2 pt-2">
          <button className="flex-1 flex items-center justify-center space-x-2 px-3 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth">
            <Icon name="Download" size={16} />
            <span className="text-sm font-medium">Export Details</span>
          </button>
          <button className="flex-1 flex items-center justify-center space-x-2 px-3 py-2 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/90 transition-smooth">
            <Icon name="ExternalLink" size={16} />
            <span className="text-sm font-medium">View on Registry</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransactionDetails;